/**
 * Tank Builder JS
 * ---------------------------------------------------------------------------
 * Drives the layered "build your own tank" experience: swatch selection per
 * slot, live stock/quantity capping, layered transparent-PNG preview
 * compositing, conditional addon questions, and a multi-line-item add to
 * cart with a shared _build_id property so items group visually in cart.
 *
 * Intended specifically for stacked/composited visual builders (parts that
 * physically assemble on top of one another, e.g. Regulator + Tank). For a
 * future builder that doesn't need image stacking, write a separate, simpler
 * module rather than overloading this one.
 *
 * Works against the markup emitted by sections/tank-builder.liquid.
 * Multiple builder instances can exist on a page; each is namespaced by
 * its section id (passed into TankBuilder.init).
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var instances = {};

  function qs(root, sel) {
    return root.querySelector(sel);
  }
  function qsa(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function Builder(rootId) {
    this.root = document.getElementById(rootId);
    if (!this.root) return;
    this.sectionId = this.root.dataset.sectionId;
    this.state = {
      slots: {},      // slotKey -> { variantId, price, available, inventoryQty, inventoryPolicy, title }
      addons: {}      // addonKey -> 'yes' | 'no'
    };
    this.slotEls = qsa(this.root, '.pb-slot');
    this.addonEls = qsa(this.root, '.pb-addon');
    this.bindEvents();
    this.initializeDefaults();
    this.updateAddonVisibility();
    this.updateSummary();
  }

  Builder.prototype.bindEvents = function () {
    var self = this;

    // Row clicks (inside the full-screen select modal)
    qsa(this.root, '.pb-row').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        self.selectSwatch(btn);
      });
    });

    // Addon toggle clicks
    qsa(this.root, '.pb-addon-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.selectAddon(btn);
      });
    });

    // The entire current-selection card is clickable (not just the
    // "Select" button text) -- matches the reference site's behavior
    // where tapping anywhere on the regulator/tank preview opens the
    // picker, not just a small button. The button itself still exists
    // visually as a clear affordance, it's just no longer the only
    // clickable area.
    qsa(this.root, '.pb-current-selection').forEach(function (card) {
      card.addEventListener('click', function () {
        var slotKey = card.closest('.pb-slot').dataset.slotKey;
        self.openModal(slotKey);
      });
    });

    // Modal close button
    qsa(this.root, '.pb-select-modal-close').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.closeModal(btn.dataset.slotKey);
      });
    });

    // Escape key closes whichever modal (if any) is currently open.
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.activeModalSlotKey) {
        self.closeModal(self.activeModalSlotKey);
      }
    });

    // Add to cart
    var addBtn = qs(this.root, '.pb-add-to-cart');
    if (addBtn) {
      addBtn.addEventListener('click', function () {
        self.addToCart();
      });
    }
  };

  /**
   * On page load, Liquid has already pre-selected a default variant per
   * slot (first in-stock item in that slot's collection) and rendered it
   * both as the collapsed "current selection" card and as the
   * pb-row-selected row within the (hidden) select modal. This reads that
   * server-rendered default into JS state and renders it onto the canvas,
   * so the preview shows a real assembled product immediately rather than
   * an empty "select options" placeholder.
   */
  Builder.prototype.initializeDefaults = function () {
    var self = this;
    qsa(this.root, '.pb-slot').forEach(function (slotEl) {
      var slotKey = slotEl.dataset.slotKey;
      var defaultBtn = slotEl.querySelector('.pb-row.pb-row-selected');
      if (defaultBtn) {
        self.applySwatchSelection(slotKey, defaultBtn);
      }
    });
  };

  /**
   * Opens the full-screen select modal for a slot (triggered by clicking
   * "Select" on the collapsed current-selection card). Locks page scroll
   * while open since the modal itself scrolls internally, matching the
   * HK Army reference behavior. The modal stays open until a row is
   * picked (selectSwatch calls closeModal) or the customer explicitly
   * closes it (X button, Escape key, or clicking the dimmed background).
   */
  Builder.prototype.openModal = function (slotKey) {
    var modal = document.getElementById('pb-modal-' + this.sectionId + '-' + slotKey);
    if (!modal) return;
    modal.hidden = false;
    document.body.classList.add('pb-modal-open');
    this.activeModalSlotKey = slotKey;
  };

  Builder.prototype.closeModal = function (slotKey) {
    var modal = document.getElementById('pb-modal-' + this.sectionId + '-' + slotKey);
    if (!modal) return;
    modal.hidden = true;
    document.body.classList.remove('pb-modal-open');
    this.activeModalSlotKey = null;
  };

  /**
   * Updates the collapsed "current selection" card (thumbnail, title,
   * price) to reflect whatever was just chosen, or the "None" state.
   */
  Builder.prototype.updateCurrentSelectionCard = function (slotKey, btn) {
    var thumbEl = document.getElementById('pb-current-thumb-' + this.sectionId + '-' + slotKey);
    var titleEl = document.getElementById('pb-current-title-' + this.sectionId + '-' + slotKey);
    var priceEl = document.getElementById('pb-current-price-' + this.sectionId + '-' + slotKey);
    if (!titleEl || !priceEl) return;

    var variantId = btn.dataset.variantId || null;
    if (!variantId) {
      titleEl.textContent = btn.querySelector('.pb-row-title') ? btn.querySelector('.pb-row-title').textContent : 'None';
      priceEl.textContent = '';
      if (thumbEl && thumbEl.tagName === 'IMG') {
        thumbEl.src = '';
        thumbEl.hidden = true;
      }
      return;
    }

    titleEl.textContent = btn.dataset.variantTitle === 'Default Title'
  ? btn.dataset.productTitle
  : btn.dataset.productTitle + ' - ' + btn.dataset.variantTitle;
    priceEl.textContent = formatMoney(parseInt(btn.dataset.price, 10));
    if (thumbEl && thumbEl.tagName === 'IMG' && btn.dataset.swatchImage) {
      thumbEl.src = btn.dataset.swatchImage;
      thumbEl.hidden = false;
    }
  };

  /**
   * Shared logic for applying a swatch's data to builder state, the
   * canvas layer, and the current-selection card. Used both by the
   * initial page-load default (initializeDefaults) and by a live click
   * (selectSwatch), so both paths stay in sync.
   */
  Builder.prototype.applySwatchSelection = function (slotKey, btn) {
    var variantId = btn.dataset.variantId || null;

    if (!variantId) {
      this.state.slots[slotKey] = null;
      this.updateLayer(slotKey, null);
    } else {
      var qtyAvailable = btn.dataset.inventoryPolicy === 'continue'
        ? Infinity
        : parseInt(btn.dataset.inventoryQty, 10);

      // Quantity per slot is always 1 -- customers build one regulator +
      // one tank per submission, not multiples of the same part. Stock
      // info (available, inventoryQty) is still tracked internally so the
      // pre-checkout safety check and out-of-stock graying keep working;
      // it's just never displayed as a number to the customer.
      this.state.slots[slotKey] = {
        variantId: variantId,
        price: parseInt(btn.dataset.price, 10),
        available: btn.dataset.available === 'true',
        inventoryQty: qtyAvailable,
        qty: 1,
        title: btn.dataset.variantTitle === 'Default Title'
  ? btn.dataset.productTitle
  : btn.dataset.productTitle + ' - ' + btn.dataset.variantTitle,
        builderImage: btn.dataset.builderImage
      };

      this.updateLayer(slotKey, btn.dataset.builderImage);
    }

    this.updateCurrentSelectionCard(slotKey, btn);
  };

  Builder.prototype.selectSwatch = function (btn) {
    var slotKey = btn.dataset.slotKey;

    // update pressed state within this slot
    var grid = document.getElementById('pb-grid-' + this.sectionId + '-' + slotKey);
    qsa(grid, '.pb-row').forEach(function (b) {
      b.classList.remove('pb-row-selected');
    });
    btn.classList.add('pb-row-selected');

    this.applySwatchSelection(slotKey, btn);

    // A new pick was made -- close the full-screen select modal and
    // return to the collapsed current-selection card view.
    this.closeModal(slotKey);

    this.updateAddonVisibility();
    this.updateSummary();
  };

  Builder.prototype.updateLayer = function (slotKey, imageUrl) {
    var img = document.getElementById('pb-layer-' + this.sectionId + '-' + slotKey);
    if (!img) return;
    if (imageUrl) {
      img.src = imageUrl;
      img.hidden = false;
    } else {
      img.src = '';
      img.hidden = true;
    }
    this.updatePlaceholderVisibility();
  };

  Builder.prototype.updatePlaceholderVisibility = function () {
    var placeholder = document.getElementById('pb-placeholder-' + this.sectionId);
    if (!placeholder) return;
    var anyVisible = qsa(this.root, '.pb-layer').some(function (img) {
      return !img.hidden;
    });
    placeholder.hidden = anyVisible;
  };

  Builder.prototype.updateAddonVisibility = function () {
    var self = this;
    this.addonEls.forEach(function (addonEl) {
      var requires = (addonEl.dataset.requiresSlots || '')
        .split(',')
        .map(function (s) { return s.trim(); })
        .filter(Boolean);
      var allFilled = requires.every(function (key) {
        return !!self.state.slots[key];
      });
      addonEl.hidden = !allFilled;
      if (!allFilled) {
        var addonKey = addonEl.dataset.addonKey;
        delete self.state.addons[addonKey];
        qsa(addonEl, '.pb-addon-btn').forEach(function (b) {
          b.classList.remove('pb-addon-selected');
        });
      }
    });
  };

  Builder.prototype.selectAddon = function (btn) {
    var addonKey = btn.dataset.addonKey;
    var value = btn.dataset.value;
    this.state.addons[addonKey] = value;

    var addonEl = document.getElementById('pb-addon-' + this.sectionId + '-' + addonKey);
    qsa(addonEl, '.pb-addon-btn').forEach(function (b) {
      b.classList.remove('pb-addon-selected');
    });
    btn.classList.add('pb-addon-selected');

    // Answering a required addon can be the only thing standing between
    // the customer and a valid checkout -- re-evaluate immediately so the
    // Add to Cart button enables the moment they answer, rather than
    // waiting for some unrelated action to trigger the next update.
    this.updateSummary();
  };

  Builder.prototype.updateSummary = function () {
    var self = this;
    var total = 0;
    var anySelected = false;

    for (var key in this.state.slots) {
      var slot = this.state.slots[key];
      if (slot) {
        anySelected = true;
        var qty = slot.qty || 1;
        total += slot.price * qty;
      }
    }

    var subtotalEl = document.getElementById('pb-subtotal-' + this.sectionId);
    if (subtotalEl) subtotalEl.textContent = formatMoney(total);

    // Any addon question that's currently visible (its requires_slots
    // condition is met) must have an answer before checkout is allowed --
    // otherwise a customer could pick a regulator + tank, see the
    // Installation question appear, and still check out without ever
    // answering it. This was a real gap: the button only checked whether
    // *a* slot was filled, never whether a visible addon was answered.
    var allVisibleAddonsAnswered = this.addonEls.every(function (addonEl) {
      if (addonEl.hidden) return true;
      var addonKey = addonEl.dataset.addonKey;
      return !!self.state.addons[addonKey];
    });

    var addBtn = qs(this.root, '.pb-add-to-cart');
    if (addBtn) addBtn.disabled = !anySelected || !allVisibleAddonsAnswered;
  };

  Builder.prototype.showError = function (msg) {
    var el = document.getElementById('pb-error-' + this.sectionId);
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
    setTimeout(function () { el.hidden = true; }, 5000);
  };

  /**
   * Attempts to open the theme's native cart drawer/slide-out after a
   * successful add-to-cart, instead of redirecting to /cart. Since the
   * exact mechanism varies by theme (Enterprise by Clean Canvas in this
   * case), this tries several common patterns in order and only falls
   * back to a full-page redirect if none appear to exist. If your theme's
   * drawer doesn't open, inspect it (right-click the cart icon -> Inspect)
   * and tell Claude what element/attribute it uses so this can be made
   * exact instead of best-effort.
   */
  /**
   * Dawn-family themes (which this Enterprise theme by Clean Canvas
   * appears to be built on, based on its <cart-items data-section=
   * "cart-drawer" data-empty="true"> markup) require the add-to-cart
   * request itself to ask for specific section IDs to be re-rendered
   * server-side, so the response includes fresh HTML for the cart drawer.
   * We still request sections here, but render the result ourselves (see
   * renderCartDrawerAndOpen below) rather than handing it to the theme's
   * own cart-drawer.js renderContents() method, which throws on this
   * specific page (it expects DOM elements that aren't present here,
   * likely because this page's native product form/blocks were hidden
   * for the builder).
   */
  Builder.prototype.getCartSectionIdsToRender = function () {
    var ids = [];
    var cartItemsEl = document.getElementById('cart-items');
    if (cartItemsEl && cartItemsEl.dataset.section) {
      ids.push(cartItemsEl.dataset.section);
    } else {
      ids.push('cart-drawer');
    }
    // Cart icon bubble/count is a near-universal second section in this
    // theme family; harmless to request even if the ID doesn't exist.
    ids.push('cart-icon-bubble');
    return ids;
  };

  Builder.prototype.renderCartDrawerAndOpen = function (parsedState) {
    var self = this;
    var renderedSomething = false;

    // NOTE: This theme's own cart-drawer.js renderContents() method is
    // unreliable on this specific page -- it loops over a fixed list of
    // section/element IDs it expects every page to have, and throws
    // (Cannot read properties of null, reading 'innerHTML') the moment it
    // hits one that's missing here, likely because this page's native
    // product form/blocks were hidden for the builder. This is a known
    // issue pattern in Dawn-family themes, not something fixable from our
    // side without editing the theme's own cart-drawer.js. So instead of
    // calling renderContents, we do a narrow, surgical update: only touch
    // the #cart-items element (which we've confirmed exists on this page)
    // using the matching section HTML from the response, and leave every
    // other element in the drawer (free shipping banner, checkout button,
    // Sezzle messaging) completely untouched.
    if (parsedState && parsedState.sections && parsedState.sections['cart-drawer']) {
      try {
        var html = parsedState.sections['cart-drawer'];
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var sourceCartItems = parsed.getElementById('cart-items');
        var targetCartItems = document.getElementById('cart-items');

        if (sourceCartItems && targetCartItems) {
          targetCartItems.innerHTML = sourceCartItems.innerHTML;
          targetCartItems.dataset.empty = sourceCartItems.dataset.empty || 'false';
          renderedSomething = true;
        }

        // Cart icon bubble (header count) is a separate, small, safe
        // target -- update it too if present, but don't fail if absent.
        var sourceBubble = parsed.getElementById('cart-icon-bubble');
        var targetBubble = document.getElementById('cart-icon-bubble');
        if (sourceBubble && targetBubble) {
          targetBubble.innerHTML = sourceBubble.innerHTML;
        }

        // The checkout button / free-shipping banner / Sezzle messaging
        // live in a separate footer div (class "cart-drawer__summary"),
        // which the theme's broken renderContents() never reaches because
        // it crashes before getting there. It has no id, so match by
        // class. This div is normally populated server-side on full page
        // loads, but stays empty after an AJAX add-to-cart on this page --
        // so we fill it in manually from the same section HTML.
        var sourceSummary = parsed.querySelector('.cart-drawer__summary');
        var targetSummary = document.querySelector('.cart-drawer__summary');
        if (sourceSummary && targetSummary) {
          targetSummary.innerHTML = sourceSummary.innerHTML;
        }

        // Free shipping progress banner (".free-shipping-container") is
        // not just stale here -- it doesn't exist in the DOM at all on
        // this page until the theme's renderContents() successfully
        // inserts it, which never happens here because that method
        // crashes first (same root cause as the two fixes above). Since
        // there's no existing target element to update, we insert the
        // source markup directly. On a normal page it appears near the
        // TOP of the drawer content (above the cart items list), not in
        // the footer -- an earlier version of this inserted it into
        // .cart-drawer__summary (the footer), which worked functionally
        // but put it in the wrong visual position. We insert it just
        // before .drawer__content--flex's first child instead, matching
        // the real layout.
        var targetShipping = document.querySelector('.free-shipping-container');
        var sourceShipping = parsed.querySelector('.free-shipping-container');
        if (!targetShipping) {
          var drawerContent = document.querySelector('.drawer__content');
          if (sourceShipping && drawerContent) {
            drawerContent.insertAdjacentHTML('afterbegin', sourceShipping.outerHTML);
          }
        } else {
          if (sourceShipping) {
            targetShipping.innerHTML = sourceShipping.innerHTML;
          }
        }
      } catch (e) {
        console.error('[TankBuilder] Manual cart-items update failed:', e);
      }
    }

    self.openCartDrawer();

    if (!renderedSomething) {
      // #cart-items wasn't found in either this page or the returned
      // section HTML -- inspect the actual ids in parsedState.sections
      // and report back.
    }
  };

  Builder.prototype.openCartDrawer = function () {
    // 1. Custom event many themes listen for.
    document.dispatchEvent(new CustomEvent('cart:open'));
    document.dispatchEvent(new CustomEvent('cart:show'));

    // 2. Common custom-element cart drawers (Dawn-derived themes, etc.)
    var drawerSelectors = [
      'cart-drawer',
      '#CartDrawer',
      '[data-cart-drawer]',
      '.cart-drawer',
      '#cart-drawer',
      '#sidebar-cart',
      '#mini-cart'
    ];
    var drawer = null;
    for (var i = 0; i < drawerSelectors.length; i++) {
      var el = document.querySelector(drawerSelectors[i]);
      if (el) { drawer = el; break; }
    }

    if (drawer) {
      if (typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      if (typeof drawer.show === 'function') {
        drawer.show();
        return;
      }
      drawer.classList.add('open', 'active', 'is-open', 'drawer--open');
      drawer.setAttribute('open', '');
      drawer.removeAttribute('hidden');
      return;
    }

    // 3. Common cart-icon trigger buttons -- simulate a click so the
    // theme's own existing open logic runs (most reliable option when
    // present, since it reuses the theme's real code rather than guessing
    // at class names). Skip plain links to avoid accidentally navigating
    // away if no drawer JS is actually attached.
    var triggerSelectors = [
      '[data-cart-icon]',
      '#cart-icon-bubble',
      '.header__icon--cart',
      '[data-drawer-trigger="cart"]',
      'button[aria-controls*="cart" i]',
      'a[href="/cart"][data-action]'
    ];
    for (var j = 0; j < triggerSelectors.length; j++) {
      var trigger = document.querySelector(triggerSelectors[j]);
      if (trigger) {
        trigger.click();
        return;
      }
    }

    // 4. Fallback: nothing recognizable found, go to the full cart page.
    window.location.href = '/cart';
  };

  Builder.prototype.showSuccess = function () {
    var el = document.getElementById('pb-success-' + this.sectionId);
    if (!el) return;
    el.hidden = false;
    setTimeout(function () { el.hidden = true; }, 4000);
  };

  /**
   * Re-validates live stock immediately before checkout via Shopify's
   * Storefront AJAX API, in case inventory changed since page load.
   */
  Builder.prototype.verifyLiveStock = function (variantId, requestedQty) {
    return fetch('/products/' + variantId + '.js')
      .catch(function () { return null; });
  };

  Builder.prototype.addToCart = function () {
    var self = this;

    // Defense in depth: don't rely solely on the button's disabled
    // attribute to enforce required addons (e.g. Installation). Some
    // assistive tech or programmatic clicks can bypass a disabled
    // button's default click-suppression in certain browsers, so check
    // again here before doing anything else.
    var unansweredRequired = this.addonEls.find(function (addonEl) {
      if (addonEl.hidden) return false;
      var addonKey = addonEl.dataset.addonKey;
      return !self.state.addons[addonKey];
    });
    if (unansweredRequired) {
      var label = unansweredRequired.querySelector('.pb-slot-label');
      var labelText = label ? label.textContent.replace('Please Select', '').trim() : 'this question';
      this.showError('Please answer "' + labelText + '" before adding to cart.');
      unansweredRequired.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    var items = [];
    var buildId = 'build_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);

    // Determine which slots are filled to decide whether to attach the
    // installation-style addon note (only meaningful when >1 real product).
    var filledSlots = [];
    for (var key in this.state.slots) {
      if (this.state.slots[key]) filledSlots.push(key);
    }

    var addonNote = null;
    for (var addonKey in this.state.addons) {
      addonNote = addonKey + ': ' + (this.state.addons[addonKey] === 'yes' ? 'Requested' : 'Not Requested');
    }

    filledSlots.forEach(function (slotKey, idx) {
      var slot = self.state.slots[slotKey];
      var qty = slot.qty || 1;

      if (qty > slot.inventoryQty) {
        self.showError('Not enough stock for ' + slot.title + '.');
        items = null;
        return;
      }

      var properties = { '_build_id': buildId, '_build_slot': slotKey };
      if (addonNote && idx === 0) {
        properties['Installation'] = self.state.addons[Object.keys(self.state.addons)[0]] === 'yes' ? 'Requested' : 'Not Requested';
      }

      items.push({
        id: slot.variantId,
        quantity: qty,
        properties: properties
      });
    });

    if (!items || items.length === 0) {
      if (items === null) return; // error already shown
      this.showError('Please select at least one option before adding to cart.');
      return;
    }

    var addBtn = qs(this.root, '.pb-add-to-cart');
    addBtn.disabled = true;
    addBtn.textContent = 'Adding...';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: items,
        sections: self.getCartSectionIdsToRender(),
        sections_url: window.location.pathname
      })
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (err) {
            throw new Error(err.description || 'Could not add to cart.');
          });
        }
        return res.json();
      })
      .then(function (parsedState) {
        self.showSuccess();
        addBtn.textContent = 'Add To Cart';
        addBtn.disabled = false;
        self.renderCartDrawerAndOpen(parsedState);
      })
      .catch(function (err) {
        self.showError(err.message || 'Something went wrong adding to cart.');
        addBtn.textContent = 'Add To Cart';
        addBtn.disabled = false;
      });
  };

  window.TankBuilder = {
    init: function (rootId) {
      if (instances[rootId]) return instances[rootId];
      var instance = new Builder(rootId);
      instances[rootId] = instance;
      return instance;
    }
  };
})();
